/**
 * SSR-compatible lazy route registry (substitution pattern).
 *
 * Each non-critical route is code-split via dynamic import(). Because
 * `renderToString` does NOT await React.lazy modules (it would emit the
 * Suspense fallback — empty HTML for crawlers), every split route uses a
 * substitution component: once its module has been preloaded (entry-server
 * awaits the import before rendering; entry-client awaits the landing
 * route's preload before hydrateRoot), the component renders synchronously.
 * If the module is not yet loaded (pure client-side navigation), it falls
 * back to React.lazy + Suspense, which is fine after hydration.
 */
import React, { lazy, Suspense } from "react";

type PageModule = { default: React.ComponentType };

function splitRoute(load: () => Promise<PageModule>): {
  Component: React.FunctionComponent;
  preload: () => Promise<void>;
} {
  let Mod: React.ComponentType | undefined;
  const preload = () =>
    load().then(m => {
      Mod = m.default;
    });
  const Lazy = lazy(load);
  const Component: React.FunctionComponent = () =>
    Mod ? (
      <Mod />
    ) : (
      <Suspense fallback={null}>
        <Lazy />
      </Suspense>
    );
  return { Component, preload };
}

const careers = splitRoute(() => import("./pages/Careers"));
const maintenancePlans = splitRoute(() => import("./pages/MaintenancePlans"));
const commercial = splitRoute(() => import("./pages/Commercial"));
const residential = splitRoute(() => import("./pages/Residential"));
const blog = splitRoute(() => import("./pages/Blog"));
const guides = splitRoute(() => import("./pages/Guides"));
const aboutUs = splitRoute(() => import("./pages/AboutUs"));
const cart = splitRoute(() => import("./pages/Cart"));
const orderConfirmation = splitRoute(() => import("./pages/OrderConfirmation"));
const adminDashboard = splitRoute(() => import("./pages/AdminDashboard"));
const septicPumping = splitRoute(() => import("./pages/SepticPumping"));
const septicInstallation = splitRoute(() => import("./pages/SepticInstallation"));
const septicInspection = splitRoute(() => import("./pages/SepticInspection"));
const septicRepairs = splitRoute(() => import("./pages/SepticRepairs"));
const septicSystemDesign = splitRoute(() => import("./pages/SepticSystemDesign"));
const faq = splitRoute(() => import("./pages/FAQ"));
const privacy = splitRoute(() => import("./pages/Privacy"));
const terms = splitRoute(() => import("./pages/Terms"));
const sitemap = splitRoute(() => import("./pages/Sitemap"));
const schedule = splitRoute(() => import("./pages/Schedule"));
const reschedule = splitRoute(() => import("./pages/Reschedule"));
const serviceAreas = splitRoute(() => import("./pages/ServiceAreas"));

/** path → split route entry. Home and NotFound stay eager in App.tsx. */
export const SPLIT_ROUTES: Record<
  string,
  { Component: React.FunctionComponent; preload: () => Promise<void> }
> = {
  "/careers": careers,
  "/maintenance-plans": maintenancePlans,
  "/commercial": commercial,
  "/residential": residential,
  "/blog": blog,
  "/guides": guides,
  "/about-us": aboutUs,
  "/cart": cart,
  "/order-confirmation": orderConfirmation,
  "/admin": adminDashboard,
  "/services/septic-pumping": septicPumping,
  "/services/septic-installation": septicInstallation,
  "/services/septic-inspection": septicInspection,
  "/services/septic-repairs": septicRepairs,
  "/services/septic-system-design": septicSystemDesign,
  "/faq": faq,
  "/privacy": privacy,
  "/terms": terms,
  "/sitemap": sitemap,
  "/schedule": schedule,
  "/reschedule/:token": reschedule,
  "/service-areas": serviceAreas,
  "/service-areas/:slug": serviceAreas,
};

/** Preload the module for a path if it is a split route (no-op otherwise). */
export function preloadRoute(path: string): Promise<void> {
  const clean = path.replace(/\/+$/, "") || "/";
  const entry = SPLIT_ROUTES[clean];
  if (entry) return entry.preload();
  // Match parameterized routes (e.g. "/reschedule/:token").
  for (const [pattern, e] of Object.entries(SPLIT_ROUTES)) {
    if (!pattern.includes(":")) continue;
    const regex = new RegExp(
      "^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$"
    );
    if (regex.test(clean)) return e.preload();
  }
  return Promise.resolve();
}
